// Step-by-step softphone guide (/parametres/telephone). Field names in quotes are the
// literal English labels shown by MicroSIP / Zoiper.
export const softphoneGuideEn = {
  title: "Step-by-step setup guide",
  intro:
    "Follow the steps in order. The values to type are personalized for you: click “Copy” and paste them into the softphone.",
  tabs_label: "Your computer",
  tab_windows: "Windows",
  tab_mac: "Mac",
  detected_windows:
    "We detected Windows. If that is not your computer, switch tabs.",
  detected_mac: "We detected a Mac. If that is not your computer, switch tabs.",
  step: "Step %{n}",
  loading: "Loading your profile…",
  no_subaccount:
    "Your administrator has not linked a VoIP.ms sub-account to your profile yet. Ask them to do it, then come back to this page to continue.",
  your_subaccount: "Your VoIP.ms sub-account: %{subaccount}",
  password_title: "Your SIP password",
  password:
    "The CRM does not store your SIP password. Ask your administrator for your SIP password (they find it in the VoIP.ms portal → Sub Accounts → Manage Sub Accounts → pencil on your sub-account). Never share it by unencrypted email; a private Teams message is fine.",
  copy: "Copy",
  copy_label: "Copy %{label}",
  copied: "%{label} copied.",
  copy_failed: "Could not copy: select the text and copy it by hand.",
  field: "Field",
  value: "Value to enter",
  empty: "(leave empty)",
  password_value: "Your SIP password (from your administrator)",
  windows: {
    s1_title: "Download and install",
    s1_a: "Open the official download page:",
    s1_b: "Pick the regular “MicroSIP” version (not “Lite”).",
    s1_c: "Open the downloaded file, then click “Next” until “Finish”.",
    s2_title: "Add your account",
    s2_a: "Open MicroSIP.",
    s2_b: "Click the ☰ menu (top right), then “Add Account”.",
    s3_title: "Fill in the account form",
    s3_a: "Fill in each field exactly as in the table. Use “Copy” to avoid typos.",
    s3_b: "Leave every other field at its default value, then click “Save”.",
    s4_title: "Check the connection",
    s4_a: "At the bottom of the MicroSIP window you should see “Online” in green.",
    s4_b: "If not, open the “Troubleshooting” section further down this page.",
    s5_title: "Let the softphone open call links",
    s5_a: "Click ☰ → “Settings”.",
    s5_b: "Tick the option that makes MicroSIP the app for “sip:” / “tel:” links (“Handle sip: and tel: links”).",
    s5_c: "If an option asks for confirmation before each call, untick it.",
    s5_d: "Click “Save”.",
    s6_title: "Choose the default app in Windows",
    s6_a: "Open Windows Settings → Apps → Default apps.",
    s6_b: "In the search box, type “SIP” and choose MicroSIP.",
    s6_c: "Do the same with “TEL” and choose MicroSIP.",
    s7_title: "Plug in your headset",
    s7_a: "Plug in your headset, then click ☰ → “Settings”.",
    s7_b: "For “Microphone” and “Speakers”, choose your headset, then click “Save”.",
  },
  mac: {
    s1_title: "Download and install",
    s1_a: "Open the official download page:",
    s1_b: "Pick “Zoiper 5 for macOS” (free version).",
    s1_c: "Open the downloaded file and drag the icon into the Applications folder.",
    s1_d: "Open the app. When macOS asks for microphone access, click “Allow”.",
    s2_title: "Sign in",
    s2_a: "On the login screen, fill in the two fields below, then click “Login”.",
    s2_b: "If the app asks for a “Hostname”, paste the server shown in the table.",
    s2_c: "Skip the proxy and “outbound authentication” screens.",
    s2_d: "When the app tests the protocols, choose “SIP UDP”.",
    s3_title: "Check the connection",
    s3_a: "You should see a green “Registered” dot next to your account.",
    s3_b: "If not, open the “Troubleshooting” section further down this page.",
    s4_title: "Let the softphone open call links",
    s4_a: "Open Settings → General and tick “Handle tel: / sip: links”.",
    s4_b: "If the option is missing: Settings → Integrations → “Register as default handler”.",
    s4_c: "If macOS asks which app should open “sip:” links, choose Zoiper.",
    s5_title: "Plug in your headset",
    s5_a: "Open Settings → Audio and choose your headset for the microphone and the speaker.",
  },
  crm: {
    title: "Test in the CRM",
    a: "Scroll down to the “Test” section of this page and click the test button.",
    b: "Your softphone should open with the test number. If you hear the call, click the confirmation button.",
    c: "To work afterwards: Call menu → “Start calling” → “Dial”.",
  },
  test_section: "Test",
  trouble: {
    title: "Troubleshooting",
    offline_q: "“Offline” or “Registration failed”",
    offline_a:
      "Check the username (%{username}), the password and the server (%{server}): one extra character is enough to block it. Also check your Internet connection (a guest Wi-Fi or a VPN can block calls).",
    forbidden_q: "“403 Forbidden”",
    forbidden_a:
      "The SIP password is wrong. Ask your administrator again and paste it with no space at the start or end.",
    nothing_q: "“Dial” opens nothing",
    nothing_a:
      "Redo the “sip:” / “tel:” links step and the default app step. If nothing changes, choose the “tel:” link type in the “Test” section below, then test again.",
    browser_q: "The browser asks which app to open",
    browser_a:
      "Choose your softphone (MicroSIP or Zoiper) and tick “Always” so it stops asking.",
    mute_q: "People cannot hear me",
    mute_a:
      "Choose the headset microphone in the softphone audio settings. On a Mac, also check the permission: System Settings → Privacy & Security → Microphone → turn on Zoiper.",
    callerid_q: "The customer sees another number",
    callerid_a:
      "That is your sub-account's displayed number (CallerID). Your administrator sets it in VoIP.ms.",
    refused_q:
      "The call fails: « Service Unavailable », « Busy Here » or « Declined »",
    refused_a:
      "The server must be exactly %{server} (your sub-account is only allowed on that server), the codec must be G.711u (PCMU) only, and « Source port » = 5060. Dial the 4443 test after each change. If it still fails, tell your administrator.",
  },
  checklist: {
    title: "Final checklist",
    installed: "The softphone is installed.",
    online: "My account is online (“Online” or “Registered”, in green).",
    links: "The softphone opens “sip:” and “tel:” links.",
    headset: "My headset is selected for the microphone and the speaker.",
    tested: "The test call opened the softphone and I heard the call.",
    path: "I know where to call: Call → Start calling → Dial.",
  },
  admin: {
    title: "Team sub-accounts (admin)",
    intro:
      "Send each rep their sub-account and SIP password in a private Teams message.",
    name: "Rep",
    subaccount: "VoIP.ms sub-account",
    can_dial: "Can make calls",
    yes: "Yes",
    no: "No",
    none: "Not linked",
    empty: "No users.",
    error: "Could not load the team.",
  },
};
